// frontend/src/components/community/ProductServiceCard.js
import React, { useState } from 'react';
import { Package, TrendingUp, Star, ShoppingBag, MessageSquare, Heart, Eye, Clock, CheckCircle, X, Edit, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ProductServiceCard = ({ 
  item, 
  type, 
  business, 
  viewMode, 
  onQuickView, 
  onOrder, 
  onInquire,
  onToggleFavorite,
  onEdit,
  onDelete,
  isFavorited,
  isOwner = false
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFavorite, setIsFavorite] = useState(isFavorited || false);

  const isProduct = type === 'product';
  const itemName = isProduct ? item.name : item.title;
  const itemPrice = isProduct ? item.price : item.basePrice;
  const itemImage = item.images?.[0];
  const itemRating = item.rating || 0;
  const reviewCount = item.reviewCount || item._count?.reviews || 0;
  const isLowStock = isProduct && item.quantity <= 5 && item.quantity > 0;
  const isOutOfStock = isProduct && item.quantity === 0;

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-2.5 w-2.5 sm:h-3 sm:w-3 ${
              star <= Math.floor(rating)
                ? 'text-yellow-400 fill-current'
                : star <= rating
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    setIsFavorite(!isFavorite);
    if (onToggleFavorite) {
      await onToggleFavorite();
    }
    toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(item);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(item.id);
    }
  };

  // Grid View Card
  const GridCard = () => (
    <div 
      className="bg-white rounded-xl shadow-neomorph overflow-hidden hover:shadow-neomorph-hover transition-all border border-primary-100/30 cursor-pointer group card-android-safe"
      onClick={() => onQuickView(item, type)}
    >
      {/* Image Section */}
      <div className="relative h-32 sm:h-40 md:h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
        {itemImage ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-200 to-gray-300" />
            )}
            <img
              src={itemImage}
              alt={itemName}
              className={`w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              onLoad={() => setImageLoaded(true)}
            />
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {isProduct ? (
              <Package className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
            ) : (
              <TrendingUp className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
            )}
          </div>
        )}

        {/* Favorite Button - Hide for owner */}
        {!isOwner && (
          <button
            onClick={handleFavoriteClick}
            className={`absolute top-2 right-2 p-1.5 rounded-lg transition-all ${
              isFavorite 
                ? 'bg-red-500 text-white shadow-md shadow-red-500/30' 
                : 'bg-white/90 backdrop-blur-sm text-gray-600 hover:bg-red-50 hover:text-red-500'
            }`}
          >
            <Heart className={`h-3.5 w-3.5 sm:h-4 sm:w-4 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
        )}

        {/* Featured Badge */}
        {item.featured && (
          <div className="absolute top-2 left-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-[8px] sm:text-xs font-bold rounded-lg shadow-md">
            FEATURED
          </div>
        )}

        {/* Type Badge */}
        <div className="absolute bottom-2 left-2 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-black/50 backdrop-blur-sm text-white text-[8px] sm:text-xs rounded-lg flex items-center">
          {isProduct ? (
            <>
              <Package className="h-2.5 w-2.5 mr-0.5" />
              Product
            </>
          ) : (
            <>
              <TrendingUp className="h-2.5 w-2.5 mr-0.5" />
              Service
            </>
          )}
        </div>

        {/* Stock Status - Only for products and for customers */}
        {isProduct && !isOwner && (
          <div className={`absolute bottom-2 right-2 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg text-[8px] sm:text-xs font-medium ${
            isOutOfStock 
              ? 'bg-red-500 text-white' 
              : isLowStock 
                ? 'bg-orange-500 text-white animate-pulse' 
                : 'bg-green-500 text-white'
          }`}>
            {isOutOfStock ? 'Out of Stock' : isLowStock ? `Only ${item.quantity} left` : `${item.quantity} in stock`}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-3 sm:p-4">
        {/* Title */}
        <h3 className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-2 mb-1">
          {itemName}
        </h3>

        {/* Category */}
        <div className="flex items-center text-[9px] sm:text-xs text-gray-500 mb-1.5 sm:mb-2">
          <span className="bg-gray-100 px-1.5 py-0.5 rounded-lg">
            {item.category}
          </span>
        </div>

        {/* Rating - Hide for owner (they don't need to see their own ratings here) */}
        {!isOwner && itemRating > 0 && (
          <div className="flex items-center justify-between mb-1.5 sm:mb-2">
            {renderStars(itemRating)}
            <span className="text-[9px] sm:text-xs text-gray-500">({reviewCount})</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-baseline gap-1 mb-2 sm:mb-3">
          <span className="text-sm sm:text-base md:text-lg font-bold text-primary-600">
            {formatAmount(itemPrice)}
          </span>
          {isProduct && item.compareAtPrice && item.compareAtPrice > itemPrice && !isOwner && (
            <span className="text-[9px] sm:text-xs text-gray-400 line-through">
              {formatAmount(item.compareAtPrice)}
            </span>
          )}
        </div>

        {/*
          Action Buttons.
          Always visible on mobile (no hover concept on touch).
          On sm+ screens, fade in on card hover via the `group` class on the
          card root above. No absolute positioning here — the buttons stay in
          normal flow so they never detach from their own card.
        */}
        <div className="flex gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200">
          {isOwner ? (
            // Owner actions: Edit and Delete
            <>
              <button
                onClick={handleEditClick}
                className="flex-1 px-2 py-1.5 rounded-lg text-[10px] sm:text-xs font-medium flex items-center justify-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all"
              >
                <Edit className="h-3 w-3" />
                Edit
              </button>
              <button
                onClick={handleDeleteClick}
                className="flex-1 px-2 py-1.5 rounded-lg text-[10px] sm:text-xs font-medium flex items-center justify-center gap-1 bg-red-50 text-red-600 hover:bg-red-100 transition-all"
              >
                <Trash2 className="h-3 w-3" />
                Delete
              </button>
            </>
          ) : (
            // Customer actions: Buy and Inquire
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOrder(item, type);
                }}
                disabled={isOutOfStock}
                className={`flex-1 px-2 py-1.5 rounded-lg text-[10px] sm:text-xs font-medium flex items-center justify-center gap-1 transition-all ${
                  isOutOfStock
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30 hover:shadow-lg'
                }`}
              >
                <ShoppingBag className="h-3 w-3" />
                {isProduct ? 'Buy' : 'Book'}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInquire(item, type);
                }}
                className="flex-1 px-2 py-1.5 bg-white text-gray-700 rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-[10px] sm:text-xs font-medium flex items-center justify-center gap-1"
              >
                <MessageSquare className="h-3 w-3" />
                Inquire
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // List View Card
  const ListCard = () => (
    <div 
      className="bg-white rounded-xl shadow-neomorph overflow-hidden hover:shadow-neomorph-hover transition-all border border-primary-100/30 cursor-pointer"
      onClick={() => onQuickView(item, type)}
    >
      <div className="flex flex-col sm:flex-row gap-3 p-3 sm:p-4">
        {/* Image */}
        <div className="relative w-full sm:w-24 md:w-32 h-24 sm:h-24 md:h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden flex-shrink-0">
          {itemImage ? (
            <img
              src={itemImage}
              alt={itemName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              {isProduct ? (
                <Package className="h-6 w-6 text-gray-400" />
              ) : (
                <TrendingUp className="h-6 w-6 text-gray-400" />
              )}
            </div>
          )}
          
          {item.featured && (
            <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white text-[8px] font-bold rounded">
              FEATURED
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-gray-800 text-sm sm:text-base line-clamp-2 flex-1">
              {itemName}
            </h3>
            {/* Favorite Button - Hide for owner */}
            {!isOwner && (
              <button
                onClick={handleFavoriteClick}
                className={`p-1.5 rounded-lg transition-all flex-shrink-0 ${
                  isFavorite 
                    ? 'bg-red-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500'
                }`}
              >
                <Heart className={`h-3.5 w-3.5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg">
              {item.category}
            </span>
            {/* Stock status - Only for products and customers */}
            {isProduct && !isOwner && (
              <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-lg ${
                isOutOfStock 
                  ? 'bg-red-100 text-red-700' 
                  : isLowStock 
                    ? 'bg-orange-100 text-orange-700' 
                    : 'bg-green-100 text-green-700'
              }`}>
                {isOutOfStock ? 'Out of Stock' : isLowStock ? `Only ${item.quantity} left` : `${item.quantity} in stock`}
              </span>
            )}
          </div>

          <p className="text-gray-600 text-xs sm:text-sm mb-2 line-clamp-2">
            {item.description}
          </p>

          {/* Rating - Hide for owner */}
          {!isOwner && itemRating > 0 && (
            <div className="flex items-center gap-2 mb-2">
              {renderStars(itemRating)}
              <span className="text-[9px] sm:text-xs text-gray-500">({reviewCount} reviews)</span>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
            <div>
              <span className="text-base sm:text-lg font-bold text-primary-600">
                {formatAmount(itemPrice)}
              </span>
              {isProduct && item.compareAtPrice && item.compareAtPrice > itemPrice && !isOwner && (
                <span className="text-xs text-gray-400 line-through ml-2">
                  {formatAmount(item.compareAtPrice)}
                </span>
              )}
            </div>

            <div className="flex gap-2">
              {isOwner ? (
                // Owner actions: Edit and Delete
                <>
                  <button
                    onClick={handleEditClick}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all"
                  >
                    <Edit className="h-3 w-3" />
                    Edit
                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                  >
                    <Trash2 className="h-3 w-3" />
                    Delete
                  </button>
                </>
              ) : (
                // Customer actions: Buy and Inquire
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onOrder(item, type);
                    }}
                    disabled={isOutOfStock}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-all ${
                      isOutOfStock
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30 hover:shadow-lg'
                    }`}
                  >
                    <ShoppingBag className="h-3 w-3" />
                    {isProduct ? 'Buy Now' : 'Book Now'}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onInquire(item, type);
                    }}
                    className="px-3 py-1.5 bg-white text-gray-700 rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium flex items-center gap-1"
                  >
                    <MessageSquare className="h-3 w-3" />
                    Inquire
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return viewMode === 'grid' ? <GridCard /> : <ListCard />;
};

export default ProductServiceCard;